import { useRef, useState } from 'react';
import { Alert, Anchor, Button, Drawer, Group, Radio, Rating, Stack, Text, Textarea } from '@mantine/core';
import { show as showIntercom } from '@intercom/messenger-js-sdk';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import type { ProductFeedbackRating, ProductFeedbackType } from 'washday-sdk';
import { useCreateProductFeedback } from '../../hooks/productFeedback/mutations';

export function ProductFeedbackAction() {
  const { t } = useTranslation('ProductFeedback');
  const [opened, setOpened] = useState(false);
  return <>
    <Button variant="subtle" color="gray" fullWidth justify="flex-start" my="xs"
      leftSection={<svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M21 15a3 3 0 0 1-3 3H9l-6 4V6a3 3 0 0 1 3-3h12a3 3 0 0 1 3 3zM8 8h8M8 12h6" />
      </svg>} onClick={() => setOpened(true)}>
      {t('action')}
    </Button>
    <ProductFeedbackDrawer opened={opened} onClose={() => setOpened(false)} />
  </>;
}

export function ProductFeedbackDrawer({ opened, onClose }: { opened: boolean; onClose: () => void }) {
  const { t } = useTranslation('ProductFeedback');
  const { pathname } = useLocation();
  const { mutateAsync, isPending } = useCreateProductFeedback();
  const [type, setType] = useState<ProductFeedbackType>('suggestion');
  const [rating, setRating] = useState<ProductFeedbackRating | null>(null);
  const [comment, setComment] = useState('');
  const [message, setMessage] = useState<'success' | 'cooldown' | 'error' | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const submitLock = useRef(false);
  const busy = isPending || submitting;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (submitLock.current || busy || !comment.trim() || comment.trim().length > 2000) return;
    submitLock.current = true;
    setSubmitting(true);
    setMessage(null);
    try {
      await mutateAsync({ type, rating, comment: comment.trim(), source: 'web-pos', context: { path: pathname } });
      setComment('');
      setType('suggestion');
      setRating(null);
      setMessage('success');
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      setMessage(status === 429 ? 'cooldown' : 'error');
    } finally {
      submitLock.current = false;
      setSubmitting(false);
    }
  };

  return <Drawer opened={opened} onClose={onClose} position="right" size="md"
    title={t('title')} closeButtonProps={{ 'aria-label': t('close') }}
    styles={{ content: { maxWidth: '100vw' } }}>
    <form onSubmit={submit}>
      <Stack gap="md">
        <Text size="sm" c="dimmed">{t('description')}</Text>
        <Anchor component="button" type="button" size="sm" ta="left" onClick={() => { onClose(); showIntercom(); }}>
          {t('support')}
        </Anchor>
        {message && <Alert color={message === 'success' ? 'green' : 'red'} role="status">{t(message)}</Alert>}
        <Radio.Group label={t('type')} value={type} onChange={(value) => setType(value as ProductFeedbackType)} withAsterisk>
          <Stack gap="xs" mt="xs">
            {(['suggestion', 'improvement', 'opinion'] as const).map((value) =>
              <Radio key={value} value={value} label={t(`types.${value}`)} disabled={busy} />)}
          </Stack>
        </Radio.Group>
        <Stack gap="xs">
          <Text size="sm" fw={500} id="product-feedback-rating">{t('rating')}</Text>
          <Group>
            <Rating value={rating ?? 0} onChange={(value) => setRating(value ? value as ProductFeedbackRating : null)}
              readOnly={busy} aria-labelledby="product-feedback-rating"
              getSymbolLabel={(value) => t('ratingStar', { rating: value })} />
            {rating !== null && <Anchor component="button" type="button" size="xs" disabled={busy} onClick={() => setRating(null)}>
              {t('clearRating')}
            </Anchor>}
          </Group>
        </Stack>
        <Textarea label={t('comment')} value={comment} onChange={(event) => setComment(event.currentTarget.value)}
          required autosize minRows={5} maxRows={12} maxLength={2000} disabled={busy}
          description={t('limit', { count: comment.length, max: 2000 })} />
        <Button type="submit" loading={busy} disabled={busy || !comment.trim() || comment.trim().length > 2000}>
          {t('submit')}
        </Button>
      </Stack>
    </form>
  </Drawer>;
}
